import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { getSequenceColor } from "@/lib/sequence-config-utils";
import { useSequenceConfig } from "@/contexts/sequence-config-context";
import { apiRequest } from "@/lib/queryClient";
import type { SequenceConfig, SequenceTemplate, PulseSequence } from "@shared/schema";

interface PulseSequenceTagsProps {
  pulseId: number | null;
  projectId: string;
  className?: string;
  selectedSequences?: string[];
  onSequenceChange?: (sequences: string[]) => void;
  isCreationMode?: boolean;
  showInlineAllLink?: boolean;
  labelText?: string;
}

export function PulseSequenceTags({ 
  pulseId, 
  projectId, 
  className = "",
  selectedSequences = [],
  onSequenceChange,
  isCreationMode = false,
  showInlineAllLink = false,
  labelText = "Sequences:"
}: PulseSequenceTagsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Clicking the "Sequences:" label toggles an expanded list that also shows
  // each sequence's name next to its (still clickable) ID button.
  const [showNames, setShowNames] = useState(false);

  // Fetch project sequences and extract sequence numbers
  const { data: projectSequences = [] } = useQuery<Array<{id: number; projectId: string; sequenceNumber: string; color: string; name?: string}>>({
    queryKey: ["/api/projects", projectId, "sequences"],
    enabled: Boolean(projectId),
  });

  // Extract sequence numbers from project sequences
  const projectSequenceNumbers = useMemo(() => 
    // De-duplicate: the sequences table can contain the same sequence number
    // multiple times per project; duplicate React keys break rendering/updates.
    Array.from(new Set(projectSequences.map(seq => seq.sequenceNumber))), 
    [projectSequences]
  );

  // No longer need global sequence config context - colors are stored with pulse sequences
  // Only use sequence templates to check which sequences exist (1-99)
  const { sequenceTemplates } = useSequenceConfig();

  // Fetch current pulse sequence tags (only if not in creation mode)
  const { data: pulseSequences = [] } = useQuery<PulseSequence[]>({
    queryKey: ["/api/pulses", pulseId, "sequences"],
    enabled: Boolean(pulseId) && !isCreationMode,
  });

  // Colors are now available directly from global context - no need for useEffect

  const invalidateSequenceQueries = () => {
    // Invalidate individual pulse sequences query (for detail dialog)
    queryClient.invalidateQueries({ queryKey: ["/api/pulses", pulseId, "sequences"] });
    
    // Invalidate batch metadata queries (for main table view)
    queryClient.invalidateQueries({ 
      predicate: q => Array.isArray(q.queryKey) && 
                     typeof q.queryKey[0] === 'string' && 
                     q.queryKey[0].includes('/api/pulses/batch-metadata') 
    });
    
    // Invalidate project tree data to ensure main table updates
    queryClient.invalidateQueries({ queryKey: ['/api/project-tree'] });
  };

  // Add sequence tag mutation
  const addSequenceMutation = useMutation({
    mutationFn: async (sequenceNumber: string) => {
      const response = await apiRequest('POST', `/api/pulses/${pulseId}/sequences`, { sequenceNumber });
      return response.json();
    },
    onSuccess: () => {
      invalidateSequenceQueries();
      toast({ title: "Sequence tag added" });
    },
    onError: (error: Error) => {
      // 409 = tag already attached (e.g. rapid double-click); the server state
      // is what the user wanted, so just refresh instead of showing an error.
      if (error.message.startsWith("409")) {
        invalidateSequenceQueries();
        return;
      }
      toast({ title: "Failed to add sequence tag", variant: "destructive" });
    },
  });

  // Remove sequence tag mutation
  const removeSequenceMutation = useMutation({
    mutationFn: async (sequenceNumber: string) => {
      const response = await apiRequest('DELETE', `/api/pulses/${pulseId}/sequences/${sequenceNumber}`);
      return response.json();
    },
    onSuccess: () => {
      invalidateSequenceQueries();
      toast({ title: "Sequence tag removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove sequence tag", variant: "destructive" });
    },
  });

  // Bulk remove all sequences mutation
  const removeAllSequencesMutation = useMutation({
    mutationFn: async (sequenceNumbers: string[]) => {
      // Remove all sequences sequentially to avoid race conditions
      for (const sequenceNumber of sequenceNumbers) {
        await apiRequest('DELETE', `/api/pulses/${pulseId}/sequences/${sequenceNumber}`);
      }
      return { removed: sequenceNumbers.length };
    },
    onSuccess: (data) => {
      invalidateSequenceQueries();
      toast({ title: `Removed ${data.removed} sequence${data.removed !== 1 ? 's' : ''}` });
    },
    onError: () => {
      toast({ title: "Failed to remove sequences", variant: "destructive" });
    },
  });

  // Bulk add all sequences mutation
  const addAllSequencesMutation = useMutation({
    mutationFn: async (sequenceNumbers: string[]) => {
      // Add all sequences sequentially
      for (const sequenceNumber of sequenceNumbers) {
        await apiRequest('POST', `/api/pulses/${pulseId}/sequences`, { sequenceNumber });
      }
      return { added: sequenceNumbers.length };
    },
    onSuccess: (data) => {
      invalidateSequenceQueries();
      toast({ title: `Added ${data.added} sequence${data.added !== 1 ? 's' : ''}` });
    },
    onError: () => {
      toast({ title: "Failed to add sequences", variant: "destructive" });
    },
  });

  // Get assigned sequences based on mode
  const assignedSequences = isCreationMode 
    ? new Set(selectedSequences) 
    : new Set(pulseSequences.map(ps => ps.sequenceNumber));

  const handleSequenceClick = (sequenceNumber: string) => {
    if (isCreationMode) {
      // In creation mode, update the local state
      const newSequences = assignedSequences.has(sequenceNumber)
        ? selectedSequences.filter(s => s !== sequenceNumber)
        : [...selectedSequences, sequenceNumber];
      onSequenceChange?.(newSequences);
    } else {
      // In edit mode, use mutations
      if (assignedSequences.has(sequenceNumber)) {
        removeSequenceMutation.mutate(sequenceNumber);
      } else {
        addSequenceMutation.mutate(sequenceNumber);
      }
    }
  };

  const handleSelectAll = () => {
    if (isCreationMode) {
      // In creation mode, toggle between all and none
      const allSelected = availableSequenceNumbers.every(seq => assignedSequences.has(seq));
      if (allSelected) {
        // Deselect all
        onSequenceChange?.([]);
      } else {
        // Select all available sequences
        onSequenceChange?.(availableSequenceNumbers);
      }
    } else {
      // In edit mode, toggle between all and none using bulk mutations
      const allSelected = availableSequenceNumbers.every(seq => assignedSequences.has(seq));
      if (allSelected) {
        // Remove all sequences using bulk mutation
        const sequencesToRemove = availableSequenceNumbers.filter(seq => assignedSequences.has(seq));
        if (sequencesToRemove.length > 0) {
          removeAllSequencesMutation.mutate(sequencesToRemove);
        }
      } else {
        // Add all unassigned sequences using bulk mutation
        const unassignedSequences = availableSequenceNumbers.filter(seq => !assignedSequences.has(seq));
        if (unassignedSequences.length > 0) {
          addAllSequencesMutation.mutate(unassignedSequences);
        }
      }
    }
  };

  // Only show sequences that are actually used in the project
  const availableSequenceNumbers = projectSequenceNumbers.filter(sequenceNumber => 
    sequenceTemplates.some(template => template.sequenceNumber === sequenceNumber)
  );

  if (availableSequenceNumbers.length === 0) return null;

  // Group sequence numbers into rows of 5
  const sequenceRows: string[][] = [];
  for (let i = 0; i < availableSequenceNumbers.length; i += 5) {
    sequenceRows.push(availableSequenceNumbers.slice(i, i + 5));
  }

  // Name lookup for the expanded ("show names") list: prefer the
  // project-specific sequence name, fall back to the template name.
  const sequenceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of sequenceTemplates) {
      if (t.name) map.set(t.sequenceNumber, t.name);
    }
    for (const s of projectSequences) {
      if (s.name) map.set(s.sequenceNumber, s.name);
    }
    return map;
  }, [projectSequences, sequenceTemplates]);

  // Check if all sequences are selected for toggle text
  const allSelected = availableSequenceNumbers.every(seq => assignedSequences.has(seq));
  const toggleText = allSelected ? "None" : "All";

  const mutationsPending = addSequenceMutation.isPending || removeSequenceMutation.isPending || addAllSequencesMutation.isPending || removeAllSequencesMutation.isPending;

  const renderSequenceButton = (sequenceNumber: string) => {
    const isAssigned = assignedSequences.has(sequenceNumber);
    // Use stored color from pulse sequence - this eliminates API calls completely
    const assignedSequence = pulseSequences.find(ps => ps.sequenceNumber === sequenceNumber);
    const color = assignedSequence?.color || '#3B82F6'; // Direct color from stored data

    return (
      <Button
        key={sequenceNumber}
        variant="ghost"
        size="sm"
        className={`h-5 w-8 px-0 py-0 text-xs font-medium rounded border whitespace-nowrap transition-all flex items-center justify-center flex-shrink-0 ${
          isAssigned 
            ? 'text-white border-0 shadow-sm' 
            : 'bg-transparent border-gray-600 text-gray-400 hover:bg-gray-700 hover:border-gray-500'
        }`}
        style={isAssigned ? { backgroundColor: color, color: 'white' } : { borderColor: color, color: color }}
        onClick={() => handleSequenceClick(sequenceNumber)}
        disabled={mutationsPending}
        title={`Sequence ${sequenceNumber.toString().padStart(2, '0')} - ${isAssigned ? 'Click to remove' : 'Click to assign'}`}
      >
        {sequenceNumber.toString().padStart(2, '0')}
      </Button>
    );
  };

  return (
    <div className={`${className}`}>
      {showInlineAllLink ? (
        <div className="flex items-start mb-1">
          <button
            onClick={() => setShowNames(prev => !prev)}
            className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            title={showNames ? "Hide sequence names" : "Show sequence names"}
            data-testid="button-toggle-sequence-names"
          >
            {labelText}
          </button>
          <button
            onClick={handleSelectAll}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors ml-2"
            disabled={addSequenceMutation.isPending || removeSequenceMutation.isPending || addAllSequencesMutation.isPending || removeAllSequencesMutation.isPending}
          >
            {toggleText}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-1">
          <div className="flex-1" />
          <button
            onClick={handleSelectAll}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            disabled={addSequenceMutation.isPending || removeSequenceMutation.isPending || addAllSequencesMutation.isPending || removeAllSequencesMutation.isPending}
          >
            {toggleText}
          </button>
        </div>
      )}
      {/* No fixed-height scroll container — all sequence rows must be fully
          visible (a fixed h-10 used to cut off the second row). */}
      <div className="w-fit max-w-full">
        {showNames ? (
          /* Expanded list: one sequence per row, clickable ID button + name */
          <div className="space-y-1 max-h-48 overflow-y-auto pr-1" data-testid="sequence-names-list">
            {availableSequenceNumbers.map((sequenceNumber) => (
              <div key={sequenceNumber} className="flex items-center gap-2">
                {renderSequenceButton(sequenceNumber)}
                <span className="text-xs text-gray-400 truncate max-w-[180px]">
                  {sequenceNames.get(sequenceNumber) || ""}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {sequenceRows.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-1 flex-wrap">
                {row.map((sequenceNumber) => renderSequenceButton(sequenceNumber))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}